import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSocket } from "../../contexts/SocketContext";
import styles from "../../styles/components/profile/NotificationsPanel.module.css";
import profileStyles from "../../styles/pages/profile/ProfilePage.module.css";
import { useUserAuth } from "../../contexts/UserAuthContext";
import { useToast } from "../../contexts/ToastContext";
import { getOwnProfilePath } from "../../utils/profilePaths";
import {
  isNotificationClickable,
  resolveNotificationNavigation,
} from "../../utils/notificationNavigation";
import {
  formatNotificationCategory,
  isHighPriority,
} from "../../utils/notificationLabels";
import { formatRelativeTime, formatDate } from "../../utils/dateFormatter";
import api from "../../utils/api/axiosInstance";
import ConfirmModal from "../common/ConfirmModal";
import useBulkSelection from "./bulk/useBulkSelection";
import BulkSelectBar, {
  SelectCheckbox,
  SelectModeButton,
} from "./bulk/BulkSelectBar";
import bulkStyles from "./bulk/BulkSelectBar.module.css";
import runBulkRequests from "../../utils/runBulkRequests";
import Modal from "../common/Modal";
import NotificationsPanelSkeleton from "./NotificationsPanelSkeleton";
import {
  FaBell,
  FaCheck,
  FaTrash,
  FaSync,
  FaUserPlus,
  FaComment,
  FaTrophy,
  FaInfoCircle,
  FaExclamationCircle,
  FaStar,
  FaShoppingBag,
  FaHandshake,
  FaClock,
  FaExclamationTriangle,
} from "react-icons/fa";

const ITEMS_PER_PAGE = 8;
const READ_FILTER = { ALL: "all", UNREAD: "unread", READ: "read" };

const countUnread = (arr) => arr.filter((n) => !n.read).length;

const isPointsRewardType = (t) =>
  t === "tier_upgrade" ||
  t === "points_tier_upgrade" ||
  t === "points_milestone" ||
  t === "points_leaderboard";

const isModerationNotification = (type, title) => {
  const t = type?.toLowerCase() || "";
  const titleLower = title?.toLowerCase() || "";

  return (
    t === "moderation" ||
    titleLower.includes("warning") ||
    titleLower.includes("removed")
  );
};

const getNotificationIcon = (type, title) => {
  const t = type?.toLowerCase() || "";
  const titleLower = title?.toLowerCase() || "";

  if (isModerationNotification(t, title)) return <FaExclamationTriangle />;
  if (isPointsRewardType(t) || titleLower.includes("tier upgrade"))
    return <FaTrophy />;
  if (t === "follow" || t === "follow-request" || titleLower.includes("follow"))
    return <FaUserPlus />;
  if (t.startsWith("marketplace")) {
    if (t.includes("offer")) return <FaHandshake />;
    return <FaShoppingBag />;
  }
  if (t === "comment" || titleLower.includes("comment")) return <FaComment />;
  if (t === "review" || titleLower.includes("review")) return <FaStar />;
  if (t === "error") return <FaExclamationCircle />;

  return <FaInfoCircle />;
};

const getIconClass = (type, title) => {
  const t = type?.toLowerCase() || "";
  const titleLower = title?.toLowerCase() || "";

  if (isModerationNotification(t, title)) return styles.iconWarning;
  if (isPointsRewardType(t) || titleLower.includes("tier upgrade"))
    return styles.iconTier;
  if (t === "follow" || t === "follow-request" || titleLower.includes("follow"))
    return styles.iconFollow;
  if (t === "comment" || titleLower.includes("comment"))
    return styles.iconComment;
  if (t.startsWith("marketplace")) return styles.iconMarketplace;
  if (t === "review" || titleLower.includes("review")) return styles.iconReview;

  return styles.iconGeneral;
};

const getTierClass = (message, title, type) => {
  const isTier =
    isPointsRewardType(type) || title?.toLowerCase().includes("tier upgrade");
  if (!isTier) return "";

  const msg = message?.toLowerCase() || "";
  if (msg.includes("platinum")) return styles.tierUpgrade;
  if (msg.includes("gold")) return styles.tierGold;
  if (msg.includes("silver")) return styles.tierSilver;
  if (msg.includes("bronze")) return styles.tierBronze;
  return styles.tierUpgrade;
};

export default function NotificationsPanel({ onUnreadChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [readFilter, setReadFilter] = useState(READ_FILTER.ALL);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUserAuth();
  const notificationsPath = getOwnProfilePath(user, "/notifications");
  const messagesPath = getOwnProfilePath(user, "/messages");
  const { showToast } = useToast();
  const { socket, isConnected } = useSocket();

  const unreadCount = useMemo(() => countUnread(items), [items]);

  const filteredItems = useMemo(() => {
    if (readFilter === READ_FILTER.UNREAD) return items.filter((n) => !n.read);
    if (readFilter === READ_FILTER.READ) return items.filter((n) => n.read);
    return items;
  }, [items, readFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / ITEMS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * ITEMS_PER_PAGE;
  const paginatedItems = filteredItems.slice(pageStart, pageStart + ITEMS_PER_PAGE);
  const visibleNotificationIds = useMemo(
    () => paginatedItems.map((item) => item._id || item.id).filter(Boolean),
    [paginatedItems],
  );
  const {
    selectMode,
    selectedIds,
    setSelectedIds,
    allVisibleSelected,
    toggle,
    selectAllVisible,
    exitSelectMode,
    toggleSelectMode,
  } = useBulkSelection(visibleNotificationIds);
  const showingStart = filteredItems.length === 0 ? 0 : pageStart + 1;
  const showingEnd = Math.min(pageStart + ITEMS_PER_PAGE, filteredItems.length);

  useEffect(() => {
    setCurrentPage(1);
  }, [readFilter, items.length]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const { data } = await api.get("/users/me/notifications", {
        withCredentials: true,
      });
      const list = Array.isArray(data) ? data : data.notifications || [];
      setItems(list);
      onUnreadChange?.(countUnread(list));
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load notifications",
      );
    } finally {
      setLoading(false);
    }
  }, [onUnreadChange]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleNewNotification = (data) => {
      const newNotification = {
        _id: `temp-${Date.now()}`,
        title: data.title,
        message: data.message,
        url: data.url,
        type: data.type || "general",
        priority: data.priority || "low",
        read: false,
        createdAt: data.createdAt || new Date().toISOString(),
      };

      setItems((prev) => {
        const exists = prev.some(
          (n) =>
            n.title === data.title &&
            n.message === data.message &&
            Math.abs(
              new Date(n.createdAt) - new Date(data.createdAt || Date.now()),
            ) < 5000,
        );
        if (exists) return prev;

        const updated = [newNotification, ...prev];
        onUnreadChange?.(countUnread(updated));
        return updated;
      });

      setTimeout(() => {
        fetchNotifications();
      }, 500);
    };

    socket.on("new_notification", handleNewNotification);
    return () => socket.off("new_notification", handleNewNotification);
  }, [socket, isConnected, fetchNotifications, onUnreadChange]);

  const markAllRead = async () => {
    if (unreadCount === 0) return;
    try {
      await api.post(
        "/users/me/notifications/read-all",
        {},
        { withCredentials: true },
      );
      setItems((prev) => {
        const next = prev.map((n) => ({ ...n, read: true }));
        onUnreadChange?.(0);
        return next;
      });
    } catch {
      showToast({ message: "Could not mark notifications as read", type: "error" });
    }
  };

  const clearAll = async () => {
    try {
      await api.delete("/users/me/notifications", { withCredentials: true });
      setItems([]);
      onUnreadChange?.(0);
      setShowClearConfirm(false);
    } catch {
      showToast({ message: "Could not clear notifications", type: "error" });
    }
  };

  const markOneRead = async (id) => {
    try {
      await api.post(
        `/users/me/notifications/${id}/read`,
        {},
        { withCredentials: true },
      );
    } catch {
      /* ignore */
    } finally {
      setItems((prev) => {
        const next = prev.map((n) =>
          n._id === id || n.id === id ? { ...n, read: true } : n,
        );
        onUnreadChange?.(countUnread(next));
        return next;
      });
    }
  };

  const deleteOne = async (id) => {
    try {
      await api.delete(`/users/me/notifications/${id}`, {
        withCredentials: true,
      });
      setItems((prev) => {
        const filtered = prev.filter((n) => (n._id || n.id) !== id);
        onUnreadChange?.(countUnread(filtered));
        return filtered;
      });
    } catch {
      showToast({ message: "Could not delete notification", type: "error" });
    }
  };

  const confirmBulkDelete = async () => {
    const ids = selectedIds;
    if (ids.length === 0) return;
    setShowBulkDeleteConfirm(false);
    setBulkBusy(true);
    try {
      const { succeeded, failed, failedIds, succeededIds } = await runBulkRequests(
        ids,
        (id) =>
          api.delete(`/users/me/notifications/${id}`, { withCredentials: true }),
      );
      if (succeeded > 0) {
        const removed = new Set(succeededIds.map(String));
        setItems((prev) => {
          const next = prev.filter(
            (item) => !removed.has(String(item._id || item.id)),
          );
          onUnreadChange?.(countUnread(next));
          return next;
        });
        showToast({
          message:
            succeeded === 1
              ? "Notification deleted"
              : `${succeeded} notifications deleted`,
          type: "success",
        });
      }
      if (failed > 0) {
        showToast({
          message: "Some notifications could not be deleted",
          type: "error",
        });
        setSelectedIds(failedIds);
      } else {
        exitSelectMode();
      }
    } finally {
      setBulkBusy(false);
    }
  };

  const focusNotificationInList = useCallback(
    (notificationId) => {
      if (!notificationId) return;

      setReadFilter(READ_FILTER.ALL);

      const index = items.findIndex(
        (item) => String(item._id || item.id) === String(notificationId),
      );
      if (index === -1) return;

      const targetPage = Math.floor(index / ITEMS_PER_PAGE) + 1;
      setCurrentPage(targetPage);
    },
    [items],
  );

  const openNotificationDetail = useCallback(
    (notification) => {
      if (!notification) return;

      const id = notification._id || notification.id;
      setSelectedNotification(notification);
      focusNotificationInList(id);
    },
    [focusNotificationInList],
  );

  useEffect(() => {
    const notificationId = location.state?.notificationId;
    const notificationFromState = location.state?.openNotification;
    if (!notificationId && !notificationFromState) return;

    const resolvedNotification =
      notificationFromState ||
      items.find(
        (item) => String(item._id || item.id) === String(notificationId),
      );

    if (!resolvedNotification) {
      if (!loading && items.length > 0) {
        navigate(location.pathname, { replace: true, state: {} });
      }
      return;
    }

    openNotificationDetail(resolvedNotification);
    navigate(location.pathname, { replace: true, state: {} });
  }, [
    items,
    loading,
    location.pathname,
    location.state,
    navigate,
    openNotificationDetail,
  ]);

  const openNotification = (notification) => {
    const id = notification._id || notification.id;

    if (!notification.read) {
      markOneRead(id);
    }

    const destination = resolveNotificationNavigation(notification, user, {
      notificationsPath,
      messagesPath,
    });

    if (destination.pathname === notificationsPath) {
      openNotificationDetail(notification);
      return;
    }

    navigate(
      { pathname: destination.pathname, hash: destination.hash },
      { state: destination.state },
    );
  };

  const closeNotificationDetail = () => {
    setSelectedNotification(null);
  };

  const selectedTypeLabel = selectedNotification?.type || "general";
  const selectedCreatedAt = selectedNotification?.createdAt || selectedNotification?.date;
  const selectedFullDate = selectedCreatedAt
    ? formatDate(selectedCreatedAt, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "";

  if (loading && items.length === 0) {
    return <NotificationsPanelSkeleton />;
  }

  if (error && items.length === 0) {
    return (
      <div className={profileStyles.contentSection}>
        <div className={styles.errorState}>
          <div className={styles.errorStateIcon}>
            <FaExclamationTriangle size={48} />
          </div>
          <h3>Unable to Load Notifications</h3>
          <p>{error}</p>
          <button type="button" className="buttonPrimary" onClick={fetchNotifications}>
            <FaClock /> Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={profileStyles.contentSection}>
      <div className={styles.panel}>
        <div className={profileStyles.sectionHeader}>
          <div>
            <h2 className={profileStyles.sectionTitle}>
              <FaBell className={profileStyles.sectionIcon} />
              Notifications
            </h2>
            {items.length > 0 && (
              <p className={profileStyles.sectionSubtitle}>
                {unreadCount > 0
                  ? `${unreadCount} unread · ${items.length} total`
                  : `${items.length} notification${items.length === 1 ? "" : "s"}`}
              </p>
            )}
          </div>
          <div className={styles.headerActions}>
            <button
              type="button"
              className="buttonSecondary"
              onClick={fetchNotifications}
              disabled={loading}
              title="Refresh notifications"
            >
              <FaSync aria-hidden /> Refresh
            </button>
            <button
              type="button"
              className="buttonSecondary"
              onClick={markAllRead}
              disabled={loading || unreadCount === 0}
              title="Mark all as read"
            >
              <FaCheck aria-hidden /> Mark all read
            </button>
            <button
              type="button"
              className="buttonSecondary"
              onClick={() => setShowClearConfirm(true)}
              disabled={loading || items.length === 0}
              title="Clear all notifications"
            >
              <FaTrash aria-hidden /> Clear all
            </button>
            {items.length > 0 && (
              <SelectModeButton
                active={selectMode}
                onClick={toggleSelectMode}
                disabled={loading || bulkBusy}
              />
            )}
          </div>
        </div>

        {selectMode && (
          <BulkSelectBar
            selectedCount={selectedIds.length}
            allSelected={allVisibleSelected}
            hasHidden={filteredItems.length > paginatedItems.length}
            busy={bulkBusy}
            onSelectAll={selectAllVisible}
            onDelete={() => setShowBulkDeleteConfirm(true)}
            noun="notifications"
          />
        )}

        {items.length > 0 && (
          <div className={styles.filterPills}>
            <button
              type="button"
              className={`${styles.filterPill} ${
                readFilter === READ_FILTER.ALL ? styles.filterPillActive : ""
              }`}
              onClick={() => setReadFilter(READ_FILTER.ALL)}
            >
              All
              <span className={styles.filterPillCount}>{items.length}</span>
            </button>
            <button
              type="button"
              className={`${styles.filterPill} ${
                readFilter === READ_FILTER.UNREAD ? styles.filterPillActive : ""
              }`}
              onClick={() => setReadFilter(READ_FILTER.UNREAD)}
            >
              Unread
              <span className={styles.filterPillCount}>{unreadCount}</span>
            </button>
            <button
              type="button"
              className={`${styles.filterPill} ${
                readFilter === READ_FILTER.READ ? styles.filterPillActive : ""
              }`}
              onClick={() => setReadFilter(READ_FILTER.READ)}
            >
              Read
              <span className={styles.filterPillCount}>
                {items.length - unreadCount}
              </span>
            </button>
          </div>
        )}

        {error && (
          <div className={`errorMessage ${styles.errorSpacing}`}>{error}</div>
        )}

        {items.length === 0 && !loading && (
          <div className={profileStyles.emptyState}>
            <div className={profileStyles.emptyStateIcon}>
              <FaBell size={64} />
            </div>
            <h3>No notifications yet</h3>
            <p className={profileStyles.emptyStateSubtext}>
              Alerts for new followers, marketplace updates, and activity will
              appear here.
            </p>
          </div>
        )}

        {filteredItems.length === 0 && items.length > 0 && (
          <div className={profileStyles.emptyState}>
            <h3>No {readFilter} notifications</h3>
            <p className={profileStyles.emptyStateSubtext}>
              Switch filters to see other notifications.
            </p>
          </div>
        )}

        {paginatedItems.length > 0 && (
          <ul className={styles.list}>
            {paginatedItems.map((n) => {
              const id = n._id || n.id;
              const clickable = isNotificationClickable(n);
              const isSelected =
                selectedNotification &&
                String(selectedNotification._id || selectedNotification.id) ===
                  String(id);
              const typeLabel = n.type || "general";
              const tierClass = getTierClass(n.message, n.title, typeLabel);
              const createdAt = n.createdAt || n.date;
              const relativeTime = formatRelativeTime(createdAt);
              const fullDate = formatDate(createdAt, {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
              });

              return (
                <li
                  key={id}
                  id={`notification-${id}`}
                  className={`${styles.item} ${
                    n.read ? styles.read : styles.unread
                  } ${tierClass} ${
                    selectMode || clickable ? styles.itemClickable : ""
                  } ${isSelected ? styles.itemHighlighted : ""} ${
                    selectMode && selectedIds.includes(id)
                      ? bulkStyles.cardSelected
                      : ""
                  }`}
                  onClick={() => {
                    if (selectMode) {
                      toggle(id);
                      return;
                    }
                    if (clickable) openNotification(n);
                  }}
                  onKeyDown={(e) => {
                    if (e.key !== "Enter" && e.key !== " ") return;
                    if (selectMode) {
                      e.preventDefault();
                      toggle(id);
                      return;
                    }
                    if (clickable) {
                      e.preventDefault();
                      openNotification(n);
                    }
                  }}
                  role={selectMode || clickable ? "button" : undefined}
                  tabIndex={selectMode || clickable ? 0 : undefined}
                  aria-pressed={selectMode ? selectedIds.includes(id) : undefined}
                >
                  <div className={styles.itemContent}>
                    <div
                      className={`${styles.itemIcon} ${getIconClass(typeLabel, n.title)}`}
                    >
                      {getNotificationIcon(typeLabel, n.title)}
                    </div>
                    <div className={styles.itemMain}>
                      <div className={styles.itemHeader}>
                        <div className={styles.titleRow}>
                          {!n.read && <span className={styles.unreadDot} aria-hidden />}
                          <strong className={styles.itemTitle}>
                            {n.title || "Notification"}
                          </strong>
                          <span className={styles.categoryBadge}>
                            {formatNotificationCategory(typeLabel)}
                          </span>
                          {isHighPriority(n.priority) && (
                            <span className={styles.priorityBadge}>Important</span>
                          )}
                        </div>
                        <div className={styles.itemActions}>
                          {selectMode && (
                            <SelectCheckbox
                              checked={selectedIds.includes(id)}
                              onChange={() => toggle(id)}
                              label={`Select ${n.title || "notification"}`}
                              disabled={bulkBusy}
                            />
                          )}
                          {!selectMode && !n.read && (
                            <button
                              type="button"
                              className={styles.iconAction}
                              onClick={(e) => {
                                e.stopPropagation();
                                markOneRead(id);
                              }}
                              title="Mark as read"
                              aria-label="Mark as read"
                            >
                              <FaCheck />
                            </button>
                          )}
                          {!selectMode && (
                          <button
                            type="button"
                            className={`${styles.iconAction} ${styles.iconActionDanger}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteOne(id);
                            }}
                            title="Delete notification"
                            aria-label="Delete notification"
                          >
                            <FaTrash />
                          </button>
                          )}
                        </div>
                      </div>
                      {(n.message || n.body) && (
                        <p className={styles.message}>{n.message || n.body}</p>
                      )}
                      <time
                        className={styles.meta}
                        dateTime={createdAt}
                        title={fullDate}
                      >
                        {relativeTime || fullDate}
                      </time>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {filteredItems.length > ITEMS_PER_PAGE && (
          <div className={styles.paginationFooter}>
            <button
              type="button"
              className={`buttonSecondary ${profileStyles.paginationButton}`}
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              disabled={safePage === 1}
            >
              Previous
            </button>
            <span className={styles.paginationInfo}>
              {showingStart}–{showingEnd} of {filteredItems.length}
            </span>
            <button
              type="button"
              className={`buttonSecondary ${profileStyles.paginationButton}`}
              onClick={() =>
                setCurrentPage((page) => Math.min(totalPages, page + 1))
              }
              disabled={safePage === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {showBulkDeleteConfirm && (
        <ConfirmModal
          message={
            selectedIds.length === 1
              ? "Delete this notification?"
              : `Delete ${selectedIds.length} notifications?`
          }
          onConfirm={confirmBulkDelete}
          onClose={() => setShowBulkDeleteConfirm(false)}
        />
      )}

      {showClearConfirm && (
        <ConfirmModal
          message="Clear all notifications? This cannot be undone."
          onConfirm={clearAll}
          onClose={() => setShowClearConfirm(false)}
        />
      )}

      {selectedNotification && (
        <Modal
          onClose={closeNotificationDetail}
          ariaLabelledBy="notification-detail-title"
        >
          <div className={styles.detailModal}>
            <div className={styles.detailHeader}>
              <div
                className={`${styles.detailIcon} ${styles.itemIcon} ${getIconClass(
                  selectedTypeLabel,
                  selectedNotification.title,
                )}`}
              >
                {getNotificationIcon(
                  selectedTypeLabel,
                  selectedNotification.title,
                )}
              </div>
              <div className={styles.detailHeaderText}>
                <h2 id="notification-detail-title" className={styles.detailTitle}>
                  {selectedNotification.title || "Notification"}
                </h2>
                <div className={styles.detailMetaRow}>
                  <span className={styles.categoryBadge}>
                    {formatNotificationCategory(selectedTypeLabel)}
                  </span>
                  {isHighPriority(selectedNotification.priority) && (
                    <span className={styles.priorityBadge}>Important</span>
                  )}
                  {selectedFullDate && (
                    <time
                      className={styles.detailTime}
                      dateTime={selectedCreatedAt}
                    >
                      {selectedFullDate}
                    </time>
                  )}
                </div>
              </div>
            </div>

            {(selectedNotification.message || selectedNotification.body) && (
              <p className={styles.detailMessage}>
                {selectedNotification.message || selectedNotification.body}
              </p>
            )}

            <div className={styles.detailActions}>
              <button
                type="button"
                className="buttonSecondary"
                onClick={closeNotificationDetail}
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
